// Canonical CSV + PDF exports for Podlab.
//
// Design rules — enforced across every PDF in the app:
//   1. ONE source of truth per entity in `PDF_ENTITY_SPECS` below. Every page,
//      the Reports > Data Exports card, and the Audit Bundle read from here so
//      "Suppliers", "Machinery", etc. produce identical PDFs no matter which
//      button the user clicked.
//   2. Client-side hydration (`hydrateRowsForEntity`) fills in the display
//      fields the backend list endpoints don't already include — e.g. joining
//      `supplier_id` -> supplier name for stock, deriving a machine's supplier
//      from its primary tech-link or service-agreement supplier fall-back.
//      This is why some columns appeared blank in Production but not Preview:
//      the underlying data existed, the PDF just wasn't hydrating it.
//   3. Consistent A4 landscape layout, explicit `columnStyles` widths + line-
//      break overflow so narrow cells (Phone, Status) stop crowding wide ones
//      (Name, Notes, Address). Header banner + footer with page numbers on
//      every page.
//   4. CSV downloads always go through the backend `/api/exports/<entity>.csv`
//      endpoints so ops can pull the authoritative raw truth for review.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { api } from "@/lib/api";
import { toast } from "sonner";

// --- Format helpers -------------------------------------------------------

const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
};

const dateOnly = (v) => (v ? String(v).slice(0, 10) : "");

const money = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Whole-dollar formatter — no ".00" tail. Used for high-value Cost columns
// (Machinery, Assets, Service agreements, Site-service visits) where the
// cents just eat column width. Falls back to 2dp only if the value has an
// actual fractional part.
const money0 = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
};

const joinList = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : (v ?? ""));

const yesNo = (v) => (v ? "yes" : "no");

// Deep read a dotted key path off a row ("primary_tech.supplier_name").
const readPath = (row, path) => {
  let cur = row;
  for (const seg of String(path).split(".")) {
    if (cur == null) return undefined;
    cur = cur[seg];
  }
  return cur;
};

// Trigger a browser download for a Blob with a friendly filename.
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// --- Backend CSV ---------------------------------------------------------

// Kick off a CSV download for an entity via the backend export route.
export const downloadCsv = async (entity, filename) => {
  try {
    const res = await api.get(`/exports/${entity}.csv`, { responseType: "blob" });
    downloadBlob(res.data, `${filename || entity}-${stamp()}.csv`);
    toast.success(`Exported ${filename || entity}.csv`);
  } catch (e) {
    toast.error(`CSV export failed`);
  }
};

// --- PDF rendering primitives --------------------------------------------

const PAGE_MARGIN = 36;
// Vertical anchors for the compact header block. Kept tight so the table
// has as much of the A4-landscape 595pt height as possible for rows.
const BANNER_H = 26;
const TITLE_Y = 46;
const SUBTITLE_Y = 60;
const TABLE_START_Y = 74;

// Header banner drawn once per section. Keeps every PDF looking the same —
// same title font, same eyebrow line, same rule.
const drawHeader = (doc, { title, subtitle, meta }) => {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(17, 17, 17);
  doc.rect(0, 0, pageW, BANNER_H, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("PODLAB · MASTER DATA EXPORT", PAGE_MARGIN, 17);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(meta, pageW - PAGE_MARGIN, 17, { align: "right" });
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(title, PAGE_MARGIN, TITLE_Y);
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(96, 112, 135);
    doc.text(subtitle, PAGE_MARGIN, SUBTITLE_Y);
    doc.setTextColor(0, 0, 0);
  }
};

// Footer: page number + timestamp on every rendered page of the PDF.
const drawFooter = (doc, footerText) => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(96, 112, 135);
  doc.text(footerText, PAGE_MARGIN, pageH - 18);
  doc.text(
    `Page ${doc.internal.getNumberOfPages()}`,
    pageW - PAGE_MARGIN,
    pageH - 18,
    { align: "right" },
  );
  doc.setTextColor(0, 0, 0);
};

// Build the autoTable body from a spec's `columns` array.
const buildBody = (rows, columns) =>
  rows.map((row) =>
    columns.map((c) => {
      const raw = c.format ? c.format(row) : readPath(row, c.key);
      if (raw == null) return "";
      if (Array.isArray(raw)) return raw.join(", ");
      return String(raw);
    }),
  );

// Convert a column spec's per-column `width` (pt) into autoTable's
// columnStyles map. Any column without a width defaults to `auto`.
const buildColumnStyles = (columns) => {
  const out = {};
  columns.forEach((c, i) => {
    const s = { overflow: "linebreak" };
    if (c.width != null) s.cellWidth = c.width;
    if (c.align) s.halign = c.align;
    if (c.mono) s.font = "courier";
    out[i] = s;
  });
  return out;
};

// Draw one entity table onto `doc`, adding pages as needed.
const renderPdfSection = (doc, spec, rows) => {
  const now = new Date();
  const meta = `${rows.length} record${rows.length === 1 ? "" : "s"} · ${now.toLocaleString()}`;
  const footerText = `${spec.title} · Podlab master data · ${now.toLocaleDateString()}`;

  drawHeader(doc, { title: spec.title, subtitle: spec.subtitle || "", meta });

  if (rows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(96, 112, 135);
    doc.text("— no records —", PAGE_MARGIN, TABLE_START_Y + 20);
    doc.setTextColor(0, 0, 0);
    drawFooter(doc, footerText);
    return;
  }

  autoTable(doc, {
    startY: TABLE_START_Y,
    head: [spec.columns.map((c) => c.header)],
    body: buildBody(rows, spec.columns),
    styles: {
      fontSize: 8,
      cellPadding: 2.5,
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
      overflow: "linebreak",
      valign: "top",
    },
    headStyles: {
      fillColor: [17, 17, 17],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8.5,
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
    },
    alternateRowStyles: { fillColor: [246, 248, 250] },
    columnStyles: buildColumnStyles(spec.columns),
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, top: BANNER_H + 6, bottom: 32 },
    didDrawPage: () => drawFooter(doc, footerText),
  });
};

// --- Client-side hydrators ------------------------------------------------
//
// These fill in the display fields the backend list endpoints don't already
// include so every PDF renders consistently regardless of which pages the
// data came from. Each hydrator returns a NEW array; input is untouched.

const hydrateStock = async (rows) => {
  const out = rows.map((r) => ({ ...r }));
  const supIds = [...new Set(out.map((r) => r.supplier_id).filter(Boolean))];
  if (supIds.length) {
    try {
      const { data: sups } = await api.get("/suppliers");
      const smap = Object.fromEntries((sups || []).map((s) => [s.id, s.name]));
      for (const r of out) {
        if (!r.supplier_name && r.supplier_id) r.supplier_name = smap[r.supplier_id] || "";
      }
    } catch { /* keep going even if suppliers fetch fails */ }
  }
  for (const r of out) {
    r.departments_str = joinList(r.departments);
    r.sub_unit = r.sub_unit || r.pack_unit || "";
    r.location = r.location || r.bin_location || "";
  }
  return out;
};

const hydrateMachines = (rows) => {
  return rows.map((r) => {
    const links = Array.isArray(r.tech_links) ? r.tech_links : [];
    const primary = links.find((t) => t?.is_primary) || links[0] || null;
    const supplierName =
      r.supplier_name ||
      r.service_agreement_supplier ||
      (primary && primary.supplier_name) ||
      "";
    return {
      ...r,
      departments_str: joinList(r.departments) || r.department || "",
      primary_tech: primary?.tech_name || "",
      supplier_name: supplierName,
    };
  });
};

const hydrateAssets = (rows) => {
  return rows.map((r) => ({
    ...r,
    departments_str: joinList(r.departments) || r.department || "",
    assignee_display:
      r.assignee_text ||
      r.assignee_name ||
      r.assignee_display ||
      "",
  }));
};

const hydrateTechnicians = (rows) => {
  return rows.map((r) => ({
    ...r,
    specialities_str: joinList(r.specialities),
    machines_linked:
      r.linked_machine_count != null
        ? r.linked_machine_count
        : (r.linked_machines || []).length,
  }));
};

const hydrateAgreements = async (rows) => {
  const out = rows.map((r) => ({ ...r, inclusions_str: joinList(r.inclusions) }));
  const needed = [...new Set(
    out.flatMap((r) => r.covered_machine_ids || []).filter(Boolean),
  )];
  if (needed.length) {
    try {
      const { data: ms } = await api.get("/machines");
      const mmap = Object.fromEntries((ms || []).map((m) => [m.id, m.name]));
      for (const r of out) {
        r.machines_str = (r.covered_machine_ids || [])
          .map((id) => mmap[id] || "")
          .filter(Boolean)
          .join(", ");
      }
    } catch { /* leave blank */ }
  } else {
    for (const r of out) r.machines_str = "";
  }
  return out;
};

const hydrateProviders = (rows) =>
  rows.map((r) => ({
    ...r,
    last_visit_display: dateOnly(r.last_visit_date),
    next_due_display: dateOnly(r.next_due_date),
    first_visit_display: dateOnly(r.first_visit_date),
  }));

const hydrateVisits = async () => {
  // Visits aren't a single backend list endpoint — walk the providers and
  // fold every provider's visit log into one flat set of rows.
  const { data: provs } = await api.get("/site-services/providers");
  const flat = [];
  for (const p of provs || []) {
    try {
      const { data: vs } = await api.get(`/site-services/providers/${p.id}/visits`);
      for (const v of vs || []) {
        flat.push({
          ...v,
          provider_name: p.name,
          provider_kind: p.kind,
          visit_date: v.visit_date || "",
          pdf_count: (v.pdf_paths || []).length,
        });
      }
    } catch { /* skip broken providers */ }
  }
  flat.sort((a, b) => (b.visit_date || "").localeCompare(a.visit_date || ""));
  return flat;
};

// --- Canonical PDF specs -------------------------------------------------
//
// Column widths sum to ~< 760pt (A4 landscape minus 2 x 36pt margins = 770pt).
// The final column always has no width (`auto`) so it soaks up the remainder
// and wraps notes cleanly.

export const PDF_ENTITY_SPECS = {
  // Column widths are hand-tuned against A4-landscape usable width
  // (842pt page − 2×36pt margins = 770pt). Every entity's defined widths
  // sum to ~665-690pt so the final `Notes` column has 80-100pt to wrap
  // into cleanly (autoTable wraps character-by-character if the auto
  // column ends up ≤0pt, which was the v190 bug).
  stock: {
    title: "Inventory · Consumables & materials",
    filename: "inventory",
    subtitle: "Every stocked consumable, material or tool.",
    fetchUrl: "/stock",
    hydrate: hydrateStock,
    columns: [
      { header: "Name", key: "name", width: 105 },
      { header: "Type", key: "type", width: 45 },
      { header: "Category", key: "category", width: 60 },
      { header: "Material", key: "material", width: 55 },
      { header: "Colour", key: "colour", width: 45 },
      { header: "Departments", key: "departments_str", width: 75 },
      { header: "Unit", key: "unit", width: 32, align: "center" },
      { header: "Qty", key: "current_qty", width: 38, align: "right", mono: true },
      { header: "Low @", key: "low_stock_threshold", width: 40, align: "right", mono: true },
      { header: "Unit cost", key: "unit_cost", format: (r) => money(r.unit_cost), width: 48, align: "right", mono: true },
      { header: "Supplier", key: "supplier_name", width: 90 },
      { header: "Bin", key: "location", width: 55 },
      { header: "Notes", key: "notes" },
    ],
  },
  suppliers: {
    title: "Suppliers directory",
    filename: "suppliers",
    subtitle: "Every registered supplier and their contact channels.",
    fetchUrl: "/suppliers",
    hydrate: null,
    columns: [
      { header: "Name", key: "name", width: 140 },
      { header: "Phone", key: "phone", width: 85 },
      { header: "After-hours", key: "after_hours_phone", width: 85 },
      { header: "Email", key: "email", width: 140 },
      { header: "Website", key: "website", width: 110 },
      { header: "Address", key: "address" },
    ],
  },
  machinery: {
    title: "Machinery register",
    filename: "machinery",
    subtitle: "Capital machines under service — supplier + agreement snapshot.",
    fetchUrl: "/machines",
    hydrate: hydrateMachines,
    columns: [
      { header: "Name", key: "name", width: 95 },
      { header: "Category", key: "category", width: 60 },
      { header: "Manufacturer", key: "manufacturer", width: 65 },
      { header: "Model", key: "model", width: 50 },
      { header: "Serial", key: "serial", width: 50 },
      { header: "Department", key: "department", width: 55 },
      { header: "Status", key: "status", width: 45 },
      { header: "Cost", key: "cost", format: (r) => money0(r.cost), width: 50, align: "right", mono: true },
      { header: "Purchased", key: "purchased_at", format: (r) => dateOnly(r.purchased_at), width: 55 },
      { header: "Warranty", key: "warranty_until", format: (r) => dateOnly(r.warranty_until), width: 55 },
      { header: "Supplier", key: "supplier_name", width: 85 },
      { header: "Notes", key: "notes" },
    ],
  },
  assets: {
    title: "Assets register",
    filename: "assets",
    subtitle: "Non-consumable assets — IT, furniture, tools assigned to staff.",
    fetchUrl: "/assets",
    hydrate: hydrateAssets,
    columns: [
      { header: "Name", key: "name", width: 95 },
      { header: "Category", key: "category", width: 55 },
      { header: "Brand", key: "brand", width: 55 },
      { header: "Model", key: "model", width: 50 },
      { header: "Serial", key: "serial", width: 55 },
      { header: "Department", key: "department", width: 55 },
      { header: "Status", key: "status", width: 45 },
      { header: "Assignee", key: "assignee_display", width: 75 },
      { header: "Location", key: "location", width: 60 },
      { header: "Cost", key: "cost", format: (r) => money0(r.cost), width: 50, align: "right", mono: true },
      { header: "Warranty", key: "warranty_until", format: (r) => dateOnly(r.warranty_until), width: 55 },
      { header: "Notes", key: "notes" },
    ],
  },
  technicians: {
    title: "Technicians directory",
    filename: "technicians",
    subtitle: "Service contacts, their supplier and machines they cover.",
    fetchUrl: "/technicians",
    hydrate: hydrateTechnicians,
    columns: [
      { header: "Name", key: "name", width: 95 },
      { header: "Role", key: "role", width: 65 },
      { header: "Supplier", key: "supplier_name", width: 100 },
      { header: "Phone", key: "phone", width: 75 },
      { header: "After-hours", key: "after_hours_phone", width: 75 },
      { header: "Email", key: "email", width: 115 },
      { header: "Specialities", key: "specialities_str", width: 100 },
      { header: "Machines", key: "machines_linked", width: 50, align: "right", mono: true },
      { header: "Notes", key: "notes" },
    ],
  },
  "service-agreements": {
    title: "Service agreements",
    filename: "service-agreements",
    subtitle: "Machine service contracts, coverage windows and pricing.",
    fetchUrl: "/service-agreements",
    hydrate: hydrateAgreements,
    columns: [
      { header: "Name", key: "name", width: 110 },
      { header: "Supplier", key: "supplier_name", width: 110 },
      { header: "Start", key: "period_start", format: (r) => dateOnly(r.period_start), width: 60 },
      { header: "End", key: "period_end", format: (r) => dateOnly(r.period_end), width: 60 },
      { header: "Cost", key: "cost", format: (r) => money0(r.cost), width: 55, align: "right", mono: true },
      { header: "Frequency", key: "payment_frequency", width: 65 },
      { header: "Auto renew", key: "auto_renew", format: (r) => yesNo(r.auto_renew), width: 55, align: "center" },
      { header: "Machines covered", key: "machines_str", width: 130 },
      { header: "Inclusions", key: "inclusions_str" },
    ],
  },
  "site-services": {
    title: "Site services · Providers",
    filename: "site-services",
    subtitle: "Pest control, fire safety, HVAC, bins, sharpening + on-call trades.",
    fetchUrl: "/site-services/providers",
    hydrate: hydrateProviders,
    columns: [
      { header: "Provider", key: "name", width: 110 },
      { header: "Kind", key: "kind", width: 70 },
      { header: "Interval", key: "interval", width: 55 },
      { header: "Contact", key: "contact_name", width: 80 },
      { header: "Phone", key: "phone", width: 75 },
      { header: "Email", key: "email", width: 115 },
      { header: "Cost / visit", key: "cost_per_visit", format: (r) => money0(r.cost_per_visit), width: 55, align: "right", mono: true },
      { header: "Last visit", key: "last_visit_display", width: 55 },
      { header: "Next due", key: "next_due_display", width: 55 },
      { header: "Notes", key: "notes" },
    ],
  },
  "site-service-visits": {
    title: "Site services · Visits log",
    filename: "site-service-visits",
    subtitle: "Full visit history across every provider.",
    fetchUrl: null, // built by walking providers — see hydrateVisits
    hydrate: hydrateVisits,
    columns: [
      { header: "Provider", key: "provider_name", width: 130 },
      { header: "Kind", key: "provider_kind", width: 80 },
      { header: "Visit date", key: "visit_date", format: (r) => dateOnly(r.visit_date), width: 70 },
      { header: "Technician", key: "technician_name", width: 115 },
      { header: "Cost", key: "cost", format: (r) => money0(r.cost), width: 55, align: "right", mono: true },
      { header: "PDFs", key: "pdf_count", width: 45, align: "right", mono: true },
      { header: "Notes", key: "notes" },
    ],
  },
};

// --- Public download API --------------------------------------------------

// Resolve the hydrated row set for an entity — either use rows the page
// already has (fast, page-scoped) or fetch fresh from the backend.
const resolveRows = async (entity, rowsIn) => {
  const spec = PDF_ENTITY_SPECS[entity];
  if (!spec) throw new Error(`Unknown PDF entity: ${entity}`);
  let rows = rowsIn;
  if (!rows) {
    if (typeof spec.hydrate === "function" && spec.fetchUrl == null) {
      // Visits — hydrator does its own fetching.
      rows = await spec.hydrate();
      return { spec, rows };
    }
    const { data } = await api.get(spec.fetchUrl);
    rows = data || [];
  }
  if (typeof spec.hydrate === "function" && spec.fetchUrl != null) {
    rows = await spec.hydrate(rows);
  }
  return { spec, rows };
};

// Download a single-entity PDF using the canonical spec.
export const downloadEntityPdf = async (entity, rowsIn) => {
  try {
    const { spec, rows } = await resolveRows(entity, rowsIn);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    renderPdfSection(doc, spec, rows);
    doc.save(`${spec.filename}-${stamp()}.pdf`);
    toast.success(`Exported ${spec.filename}.pdf`);
  } catch (e) {
    console.error("PDF export failed", e);
    toast.error("PDF export failed");
  }
};

// Legacy adapter — some callers still pass `{ title, rows, columns, filename }`.
// We render them with the same header/footer look for visual consistency.
export const downloadPdf = ({ title, rows, columns, filename }) => {
  const spec = { title, filename: filename || "export", columns };
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  renderPdfSection(doc, spec, rows || []);
  doc.save(`${spec.filename}-${stamp()}.pdf`);
  toast.success(`Exported ${spec.filename}.pdf`);
};

// One dossier — cover + one section per entity — for the 6-monthly audit
// walk-through. Every section uses the same canonical spec as the
// per-page export, so nothing drifts.
const BUNDLE_ORDER = [
  "stock",
  "suppliers",
  "machinery",
  "assets",
  "technicians",
  "service-agreements",
  "site-services",
  "site-service-visits",
];

export const downloadAuditBundle = async () => {
  toast.info("Building audit bundle… this can take a few seconds");
  try {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const now = new Date();
    const pretty = now.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Cover page
    doc.setFillColor(17, 17, 17);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), BANNER_H, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("PODLAB · MASTER DATA EXPORT", PAGE_MARGIN, 17);
    doc.setFont("helvetica", "normal");
    doc.text(`Bundle · ${pretty}`, doc.internal.pageSize.getWidth() - PAGE_MARGIN, 17, { align: "right" });
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(26);
    doc.text("Audit bundle", PAGE_MARGIN, 76);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(96, 112, 135);
    doc.text(`Master-data snapshot · ${pretty}`, PAGE_MARGIN, 94);
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(
      "One dossier for the 6-monthly departmental sign-off. Each section",
      PAGE_MARGIN,
      124,
    );
    doc.text(
      "lists everything the app currently holds for that entity — verify with",
      PAGE_MARGIN,
      138,
    );
    doc.text(
      "the relevant department and flag anything stale before the next review.",
      PAGE_MARGIN,
      152,
    );

    // Resolve every entity in parallel; each falls back to `[]` if the fetch
    // fails so one broken endpoint doesn't kill the whole bundle.
    const resolved = await Promise.all(
      BUNDLE_ORDER.map(async (entity) => {
        try {
          return await resolveRows(entity, null);
        } catch {
          return { spec: PDF_ENTITY_SPECS[entity], rows: [] };
        }
      }),
    );

    // Contents list on the cover.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Contents", PAGE_MARGIN, 184);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    resolved.forEach(({ spec, rows }, i) => {
      doc.text(
        `${i + 1}.  ${spec.title}  —  ${rows.length} record${rows.length === 1 ? "" : "s"}`,
        PAGE_MARGIN + 18,
        204 + i * 16,
      );
    });

    drawFooter(doc, `Podlab audit bundle · ${pretty}`);

    // One page per entity.
    for (const { spec, rows } of resolved) {
      doc.addPage();
      renderPdfSection(doc, spec, rows);
    }

    doc.save(`podlab-audit-bundle-${stamp()}.pdf`);
    toast.success("Audit bundle downloaded");
  } catch (e) {
    console.error("Audit bundle failed", e);
    toast.error("Audit bundle failed");
  }
};
